<template>
    <div id="room-videos" ref="room_videos"
         class="tw-absolute tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center">
        <!-- Screen share -->
        <screen-video-player
            ref="screen"
            :full_width="!has_active_remote_camera_track"
            v-show="show_screenshare"
            :volume="live_room.volume"
            @onMounted="attachSubscribedTracks"
        />

        <div
            id="participant-videos"
            v-show="show_participant_videos"
            class="tw-flex tw-items-center"
            :class="[show_screenshare ? 'tw-flex-col tw-w-3/12 gap-2-5 tw-mx-4' : 'tw-w-full participant-wrapper', {'tw-h-full' : !show_screenshare && !multiple_presenters}]"
        >
            <!-- Local video -->
            <participant-video
                v-if="livekit_room.localParticipant.permissions.canPublish"
                :show_screenshare="show_screenshare"
                :multiple_presenters="multiple_presenters"
                :is_local="true"
                :participant="livekit_room.localParticipant"
                :live_room="live_room"
                :full_viewport="full_viewport"
                :debug="debug"
            />

            <!-- Remote video(s) -->
            <participant-video
                v-for="participant in remote_publishers"
                :key="participant.sid"
                :show_screenshare="show_screenshare"
                :participant="participant"
                :full_viewport="full_viewport"
                :live_room="live_room"
                :multiple_presenters="multiple_presenters"
                :debug="debug"
            />
        </div>
    </div>
</template>

<script>
import ParticipantVideo from "./RoomVideoParticipant.vue";
import ScreenVideoPlayer from "./RoomVideoScreenshare.vue";
import {tryToPlay} from "../../mixins/v2/LiveHelpers";
import {ParticipantEvent, RoomEvent, Track} from "livekit-client"

export default {
    name: 'RoomVideos',
    components: {
        ScreenVideoPlayer,
        'participant-video': ParticipantVideo,
    },
    data() {
        return {
            screen_is_muted: true,
        }
    },
    props: {
        debug: {
            required: false,
            type: Boolean,
            default: false,
        },
        full_viewport: {
            required: true,
            type: Boolean
        },
        live_room: {
            required: true,
            type: Object,
        },
        livekit_room: {
            required: true,
            type: Object,
        },
        live_room_participant: {
            required: true,
            type: Object,
        },
        publishers: {
            required: true,
            type: Array,
        },
        is_connected: {
            default: false,
            type: Boolean,
        },
    },
    computed: {
        show_screenshare() {
            return !this.screen_is_muted && this.live_room.local_screen_is_muted;
        },
        remote_publishers() {
            if (this.publishers.length <= 0) {
                return [];
            }

            const remote_publsisher = this.publishers.filter(publisher => {
                return publisher.sid !== this.livekit_room.localParticipant.sid
            });

            return remote_publsisher;
        },
        should_not_show_participant_cameras() {
            return this.show_screenshare && !this.live_room.local_camera_is_muted && !this.has_active_remote_camera_track;
        },
        show_participant_videos() {
            return !this.show_screenshare || this.show_screenshare && (!this.live_room.local_camera_is_muted || this.has_active_remote_camera_track);
        },
        multiple_presenters() {
            return this.publishers.length >= 2;
        },
        has_active_remote_camera_track() {
            const tracks = this.live_room.tracks_subscribed;
            return tracks.some(track => track.source === Track.Source.Camera && !track.isMuted);
        }
    },
    methods: {
        attachSubscribedTracks() {
            this.live_room.tracks_subscribed.forEach(track => {
                if (!track.is_attached && (track.source === Track.Source.ScreenShare || track.source === Track.Source.ScreenShareAudio)) {
                    this.setScreenTrackMuted(track.source, track.isMuted)
                    this.attachScreenTrack(track);
                    this.live_room.screen_sharer_sid = track.sid;
                }
            })
        },
        async handleRequestScreenShareTakeover(requester_sid) {
            const current_tracks = this.livekit_room.localParticipant.getTracks();
            current_tracks.forEach(publication => {
                if (publication.track.source === Track.Source.ScreenShare || publication.track.source === Track.Source.ScreenShareAudio) {
                    this.livekit_room.localParticipant.unpublishTrack(publication.track);
                }
            })
            this.sendScreenShareAcceptedEvent(requester_sid);
        },
        sendScreenShareAcceptedEvent(requester_sid) {
            this.live_room.presence_channel.whisper('screenShareTakeoverResponse', {
                requester_sid,
                accepted: true,
            });
        },
        setScreenTrackMuted(source, is_muted, local = false) {
            switch (source) {
                case 'screen_share':
                    if (local) {
                        this.live_room.local_screen_is_muted = is_muted;
                    }
                    this.screen_is_muted = is_muted;
                    break;
                case 'screen_share_audio':
                    if (local) {
                        this.live_room.local_screen_audio_is_muted = is_muted;
                    }
                    this.screen_audio_is_muted = is_muted;
                    break;
                default:
                    break;
            }
        },
        async attachScreenTrack(track, local = false) {
            const screen_el = this.$refs.screen.$refs.video;
            const audio_el = this.$refs.screen.$refs.audio;
            const subscribed_track = this.live_room.tracks_subscribed.find(t => t.sid === track.sid);

            switch (track.source) {
                case 'screen_share':
                    await track.attach(screen_el);
                    subscribed_track.is_attached = true;
                    break;
                case 'screen_share_audio':
                    if (!local) {
                        await track.attach(audio_el);
                        subscribed_track.is_attached = true;
                    }
                    break;
                default:
                    break;
            }
        },
        handleTrackSubscribed(remoteTrack, remoteTrackPublication, remoteParticipant) {
            this.setScreenTrackMuted(remoteTrack.source, remoteTrack.isMuted)
            this.attachScreenTrack(remoteTrack);
            if (remoteTrack.source === Track.Source.ScreenShare || remoteTrack.source === Track.Source.ScreenShareAudio) {
                this.live_room.screen_sharer_sid = remoteParticipant.sid;
            }
        },
        handleTrackUnsubscribed(remoteTrack, remoteTrackPublication) {
            this.setScreenTrackMuted(remoteTrackPublication.source, true);
            remoteTrack.detach();
            if (remoteTrack.source === Track.Source.ScreenShare || remoteTrack.source === Track.Source.ScreenShareAudio) {
                this.live_room.screen_sharer_sid = null;
            }
        },
        handleLocalTrackPublished(localTrackPublication) {
            this.setScreenTrackMuted(localTrackPublication.track.source, localTrackPublication.track.isMuted, true)
            this.attachScreenTrack(localTrackPublication.track, true);
            if (localTrackPublication.source === Track.Source.ScreenShare || localTrackPublication.source === Track.Source.ScreenShareAudio) {
                this.live_room.screen_sharer_sid = this.livekit_room.localParticipant.sid;
            }
        },
        handleLocalTrackUnpublished(localTrackPublication) {
            this.setScreenTrackMuted(localTrackPublication.source, true, true);
            if (localTrackPublication.track) {
                localTrackPublication.track.detach();
            }
            if (localTrackPublication.source === Track.Source.ScreenShare || localTrackPublication.source === Track.Source.ScreenShareAudio) {
                this.live_room.screen_sharer_sid = null;
            }
        },

        setupLiveKitEventHandlers() {
            this.livekit_room.on(ParticipantEvent.LocalTrackPublished, this.handleLocalTrackPublished);
            this.livekit_room.on(ParticipantEvent.LocalTrackUnpublished, this.handleLocalTrackUnpublished);
            this.livekit_room.on(RoomEvent.TrackSubscribed, this.handleTrackSubscribed);
            this.livekit_room.on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed);
        }
    },
    mounted() {
        this.live_room.presence_channel.listenForWhisper('requestScreenShareTakeover', (event) => {
            if (event.current_screen_sharer_sid === this.livekit_room.localParticipant.sid) {
                this.handleRequestScreenShareTakeover(event.requester_sid)
            }
        });

        this.setupLiveKitEventHandlers();
    }
};
</script>

<style scoped>
.gap-2-5 {
    gap: 0.571rem;
}

.participant-wrapper {
    min-height: 353px;
}

.room-videos {
    top: 0;
    width: 100%;
}
</style>
